import * as THREE from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EOpenPoseCursorPlane } from '../enums/e_open_pose_cursor_plane';

const {
  PLANE_CURSOR_PLANE_SIZE_WORLD,
  PLANE_CURSOR_SMALL_OFFSET,
  PLANE_CURSOR_XY_PLANE_COLOR,
  CURSOR_PLANE_RENDER_ORDER,
  PLANE_CURSOR_XZ_PLANE_COLOR,
  PLANE_CURSOR_YZ_PLANE_COLOR,
  PLANE_CURSOR_AXIS_HINT_LENGTH,
  CURSOR_PLANE_LINE_RENDER_ORDER,
} = ConstantsOpenPose;

export class OpenPoseCursorPlane extends THREE.Group {
  /*
   * FIELDS
   */
  private plane_meshes_list: THREE.Mesh[];
  private frame_buffer_geometries_list: THREE.BufferGeometry[];
  private solid_plane_geometries_list: THREE.PlaneGeometry[];
  private axis_lines_list: THREE.Line[];

  /*
   * METHODS
   */
  constructor() {
    super();

    this.plane_meshes_list = [];
    this.frame_buffer_geometries_list = [];
    this.solid_plane_geometries_list = [];
    this.axis_lines_list = [];

    this.name = ConstantsOpenPose.PLANE_CURSOR_NAME;
    this.create_cursor_geometry_and_materials();
  }

  private create_frame_buffer_geometry(square_size_world: number): THREE.BufferGeometry {
    const size_outer = square_size_world;
    const size_inner = square_size_world * ConstantsOpenPose.PLANE_CURSOR_SIZE_INNER_MULTIPLIER;

    const vertices: number[] = [];
    const indices: number[] = [];

    vertices.push(
      -size_outer / 2,
      -size_outer / 2,
      0,
      size_outer / 2,
      -size_outer / 2,
      0,
      size_outer / 2,
      size_outer / 2,
      0,
      -size_outer / 2,
      size_outer / 2,
      0,

      -size_inner / 2,
      -size_inner / 2,
      0,
      size_inner / 2,
      -size_inner / 2,
      0,
      size_inner / 2,
      size_inner / 2,
      0,
      -size_inner / 2,
      size_inner / 2,
      0,
    );

    indices.push(0, 1, 4, 1, 5, 4, 1, 2, 5, 2, 6, 5, 2, 3, 6, 3, 0, 7, 0, 4, 7);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  private create_cursor_geometry_and_materials() {
    const plane_size_world = PLANE_CURSOR_PLANE_SIZE_WORLD;
    const plane_opacity_default = 0.7;
    const small_offset = PLANE_CURSOR_SMALL_OFFSET;

    const xy_frame_geometry = this.create_frame_buffer_geometry(plane_size_world);
    const xy_solid_geometry = new THREE.PlaneGeometry(plane_size_world, plane_size_world);
    const xy_material = new THREE.MeshBasicMaterial({
      color: PLANE_CURSOR_XY_PLANE_COLOR,
      transparent: true,
      opacity: plane_opacity_default,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    const xy_mesh = new THREE.Mesh(xy_frame_geometry, xy_material);
    xy_mesh.position.set(small_offset, small_offset, 0);
    xy_mesh.name = EOpenPoseCursorPlane.XY;
    xy_mesh.renderOrder = CURSOR_PLANE_RENDER_ORDER;

    this.plane_meshes_list.push(xy_mesh);
    this.frame_buffer_geometries_list.push(xy_frame_geometry);
    this.solid_plane_geometries_list.push(xy_solid_geometry);
    this.add(xy_mesh);

    const xz_frame_geometry = this.create_frame_buffer_geometry(plane_size_world);
    const xz_solid_geometry = new THREE.PlaneGeometry(plane_size_world, plane_size_world);
    const xz_material = new THREE.MeshBasicMaterial({
      color: PLANE_CURSOR_XZ_PLANE_COLOR,
      transparent: true,
      opacity: plane_opacity_default,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    const xz_mesh = new THREE.Mesh(xz_frame_geometry, xz_material);
    xz_mesh.rotation.x = -Math.PI / 2;
    xz_mesh.position.set(small_offset, 0, small_offset);
    xz_mesh.name = EOpenPoseCursorPlane.XZ;
    xz_mesh.renderOrder = CURSOR_PLANE_RENDER_ORDER;

    this.plane_meshes_list.push(xz_mesh);
    this.frame_buffer_geometries_list.push(xz_frame_geometry);
    this.solid_plane_geometries_list.push(xz_solid_geometry);
    this.add(xz_mesh);

    const yz_frame_geometry = this.create_frame_buffer_geometry(plane_size_world);
    const yz_solid_geometry = new THREE.PlaneGeometry(plane_size_world, plane_size_world);
    const yz_material = new THREE.MeshBasicMaterial({
      color: PLANE_CURSOR_YZ_PLANE_COLOR,
      transparent: true,
      opacity: plane_opacity_default,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    const yz_mesh = new THREE.Mesh(yz_frame_geometry, yz_material);
    yz_mesh.rotation.y = Math.PI / 2;
    yz_mesh.position.set(0, small_offset, small_offset);
    yz_mesh.name = EOpenPoseCursorPlane.YZ;
    yz_mesh.renderOrder = CURSOR_PLANE_RENDER_ORDER;

    this.plane_meshes_list.push(yz_mesh);
    this.frame_buffer_geometries_list.push(yz_frame_geometry);
    this.solid_plane_geometries_list.push(yz_solid_geometry);
    this.add(yz_mesh);

    this.create_axis_hint_lines();
  }

  private create_axis_hint_lines() {
    const axis_hint_length = PLANE_CURSOR_AXIS_HINT_LENGTH;

    const x_points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(axis_hint_length, 0, 0)];
    const x_geometry = new THREE.BufferGeometry().setFromPoints(x_points);
    const x_material = new THREE.LineBasicMaterial({ color: 0xff0000, depthTest: false, depthWrite: false, linewidth: 3 });
    const x_line = new THREE.Line(x_geometry, x_material);
    x_line.renderOrder = CURSOR_PLANE_LINE_RENDER_ORDER;
    this.axis_lines_list.push(x_line);
    this.add(x_line);

    const y_points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, axis_hint_length, 0)];
    const y_geometry = new THREE.BufferGeometry().setFromPoints(y_points);
    const y_material = new THREE.LineBasicMaterial({ color: 0x00ff00, depthTest: false, depthWrite: false, linewidth: 3 });
    const y_line = new THREE.Line(y_geometry, y_material);
    y_line.renderOrder = CURSOR_PLANE_LINE_RENDER_ORDER;
    this.axis_lines_list.push(y_line);
    this.add(y_line);

    const z_points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, axis_hint_length)];
    const z_geometry = new THREE.BufferGeometry().setFromPoints(z_points);
    const z_material = new THREE.LineBasicMaterial({ color: 0x0000ff, depthTest: false, depthWrite: false, linewidth: 3 });
    const z_line = new THREE.Line(z_geometry, z_material);
    z_line.renderOrder = CURSOR_PLANE_LINE_RENDER_ORDER;
    this.axis_lines_list.push(z_line);
    this.add(z_line);
  }

  public get_plane_index(plane_name: string): number {
    return this.plane_meshes_list.findIndex(plane_mesh => plane_mesh.name === plane_name);
  }

  public set_plane_to_solid(plane_index: number) {
    if (plane_index >= 0 && plane_index < this.plane_meshes_list.length)
      this.plane_meshes_list[plane_index].geometry = this.solid_plane_geometries_list[plane_index];
  }

  public set_plane_to_frame(plane_index: number) {
    if (plane_index >= 0 && plane_index < this.plane_meshes_list.length)
      this.plane_meshes_list[plane_index].geometry = this.frame_buffer_geometries_list[plane_index];
  }

  public dispose() {
    this.plane_meshes_list.forEach(mesh => (mesh.material as THREE.Material).dispose());
    this.frame_buffer_geometries_list.forEach(geometry => geometry.dispose());
    this.solid_plane_geometries_list.forEach(geometry => geometry.dispose());

    this.axis_lines_list.forEach(line => {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    });
  }
}
